import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useConfirmedMutation } from '../../lib/confirm';
import { MapContainer, TileLayer, Marker, Polyline, Tooltip as LTooltip, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import dayjs from 'dayjs';
import {
  AlertTriangle, Camera, CheckCircle2, Clock, Crosshair, ExternalLink, LogIn, LogOut,
  MapPin, PackageCheck, ScanFace, ShieldAlert, ShieldCheck, Sprout, Store, Timer, UserCog,
} from 'lucide-react';
import { Modal, Button, Select, Tag, Popconfirm, Image, Spin, message, cn } from '../../ui';
import { orderStatusLabel } from '../../lib/statusTone';
import { request } from '../../api/client';
import { requestAllPages } from '../../api/allPages';
import { formatIst, formatIstTime } from '../../lib/time';
import { inr } from '../../lib/format';
import { useReverseGeocode } from '../../lib/reverseGeocode';
import { usePermission } from '../../store/auth';
import { RoleBadge } from '../../components/RoleBadge';
import { OrderLink, RetailerLink } from '../../components/EntityLinks';
import { MAP_TILE_URL, MAP_TILE_ATTRIBUTION } from '../../lib/mapTiles';
import { PALETTE } from '../../lib/palette';
import { STATUS_COLOR, STATUS_OPTIONS, SOURCE_TAG, fmtIdle, fmtWorked, mapsUrl } from './attendanceMeta';

/* ─────────────────────────────── map pieces ─────────────────────────────── */

// Leaflet's bundled marker images don't resolve under the bundler, so every pin is an inline SVG.
const pin = (color: string) => L.divIcon({
  className: '',
  iconSize: [24, 24],
  iconAnchor: [12, 24],
  html: `<svg viewBox="0 0 24 32" width="24" height="24" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 0C5.4 0 0 5.4 0 12c0 8.4 12 20 12 20s12-11.6 12-20C24 5.4 18.6 0 12 0z" fill="${color}" stroke="white" stroke-width="1.5"/>
    <circle cx="12" cy="12" r="5" fill="white"/></svg>`,
});
const IN_ICON = pin(PALETTE.brand[600]);    // green — where the shift started
const OUT_ICON = pin(PALETTE.danger[500]);  // red   — where it ended
const LAST_ICON = pin(PALETTE.sky[500]);    // blue  — latest known position of the day
const VISIT_ICON = pin(PALETTE.plum[500]);  // violet— a customer visited in between

/** Leaflet mis-measures while the dialog is still animating in — resize, then frame the pins. */
function FitPoints({ points }: { points: Array<[number, number]> }) {
  const map = useMap();
  const key = points.map((p) => p.join(',')).join('|');
  useEffect(() => {
    const t = setTimeout(() => {
      map.invalidateSize();
      if (points.length === 1) map.setView(points[0], 16);
      else if (points.length > 1) map.fitBounds(points as any, { padding: [30, 30], maxZoom: 16 });
    }, 220);
    return () => clearTimeout(t);
  }, [map, key]); // eslint-disable-line react-hooks/exhaustive-deps
  return null;
}

/* ────────────────────────── small presentational parts ────────────────────────── */

function Section({ title, hint, children, right }: { title: string; hint?: string; children: ReactNode; right?: ReactNode }) {
  return (
    <section className="mt-5">
      <div className="mb-2 flex flex-wrap items-end justify-between gap-x-3 gap-y-1">
        <div className="min-w-0">
          <h3 className="type-overline text-subtle">{title}</h3>
          {hint && <p className="mt-0.5 type-caption text-subtle">{hint}</p>}
        </div>
        {right}
      </div>
      {children}
    </section>
  );
}

const CHECK_TONE = {
  good: 'border-brand-500/25 bg-brand-50/60 text-brand-700 dark:bg-brand-900/20 dark:text-brand-300',
  bad: 'border-danger-500/30 bg-danger-500/8 text-danger-600 dark:text-danger-500',
  idle: 'border-line bg-surface-2 text-muted',
} as const;

/** One anti-fraud signal, stated as a VERDICT ("Not detected") rather than a raw value ("false"). */
function Check({ icon, label, value, tone, title }: { icon: ReactNode; label: string; value: string; tone: keyof typeof CHECK_TONE; title?: string }) {
  return (
    <div title={title} className={cn('flex items-center gap-2 rounded-control border px-2.5 py-2', CHECK_TONE[tone])}>
      <span className="shrink-0 [&>svg]:size-4">{icon}</span>
      <div className="min-w-0">
        <div className="type-caption text-subtle">{label}</div>
        <div className="truncate type-label-strong">{value}</div>
      </div>
    </div>
  );
}

/** A resolved street address with its map link — never a bare lat/lng, which nobody can read. */
function Where({ lat, lng, address, resolving }: { lat?: string | null; lng?: string | null; address?: string | null; resolving?: boolean }) {
  const url = mapsUrl(lat, lng);
  if (!url) return <span className="type-caption text-subtle">No location captured</span>;
  return (
    <div className="min-w-0">
      <div className="flex items-start gap-1.5 text-content">
        <MapPin className="mt-0.5 size-3.5 shrink-0 text-subtle" />
        <span className="line-clamp-2 type-caption leading-snug">
          {resolving
            ? <span className="text-subtle">Resolving address…</span>
            : (address || `${Number(lat).toFixed(5)}, ${Number(lng).toFixed(5)}`)}
        </span>
      </div>
      <a href={url} target="_blank" rel="noreferrer"
        className="mt-1 inline-flex items-center gap-1 type-caption-strong text-brand-700! hover:underline dark:text-brand-400!">
        Open in Maps <ExternalLink className="size-3" />
      </a>
    </div>
  );
}

/**
 * One end of the shift — the punch time, the selfie taken with it, and where it happened, in one
 * card. These three used to be unrelated rows (a time near the top, a "Map (18.52, 73.85)" link in
 * the middle, a selfie at the bottom), so the reader had to reassemble the moment themselves.
 */
function PunchCard({ kind, at, sameDay, selfie, children }: {
  kind: 'in' | 'out'; at?: string | null; sameDay: boolean; selfie?: string | null; children: ReactNode;
}) {
  const isIn = kind === 'in';
  return (
    <div className="rounded-card border border-line bg-surface shadow-e1 p-3">
      <div className="flex items-center gap-2">
        <span className={cn('grid size-6 shrink-0 place-items-center rounded-full [&>svg]:size-3.5',
          isIn ? 'bg-brand-50 text-brand-700 dark:bg-brand-900/35 dark:text-brand-300'
            : 'bg-danger-500/10 text-danger-600 dark:text-danger-500')}>
          {isIn ? <LogIn /> : <LogOut />}
        </span>
        <span className="type-overline text-subtle">{isIn ? 'Check-in' : 'Check-out'}</span>
      </div>

      <div className="mt-2.5 flex items-center gap-3">
        {selfie ? (
          <Image src={selfie} alt={isIn ? 'Check-in selfie' : 'Check-out selfie'} width={52} height={52}
            className="size-13 shrink-0 rounded-control border border-line object-cover" />
        ) : (
          <span className="grid size-13 shrink-0 place-items-center rounded-control border border-dashed border-line text-subtle"
            title="No selfie captured">
            <Camera className="size-4" />
          </span>
        )}
        <div className="min-w-0">
          <div className={cn('type-metric-sm', at ? 'text-content' : 'text-subtle')}>
            {at ? formatIstTime(at) : (isIn ? 'Not punched' : 'Still open')}
          </div>
          <div className="type-caption text-subtle">
            {at ? (sameDay ? (isIn ? 'Shift started' : 'Shift ended') : formatIst(at, 'DD MMM YYYY')) : '—'}
          </div>
        </div>
      </div>

      <div className="mt-2.5 border-t border-line pt-2.5">{children}</div>
    </div>
  );
}

/** A highlighted note the reviewer must not miss (on-behalf audit, auto-closed shift, open shift). */
function Callout({ tone, icon, title, children }: { tone: 'plum' | 'gold' | 'sky'; icon: ReactNode; title: string; children?: ReactNode }) {
  const TONES = {
    plum: 'border-plum-500/30 bg-plum-500/8',
    gold: 'border-harvest-400/40 bg-harvest-50/70 dark:bg-harvest-400/10',
    sky: 'border-sky-500/30 bg-sky-500/8',
  } as const;
  const ICON = { plum: 'text-plum-500 dark:text-plum-400', gold: 'text-harvest-600 dark:text-harvest-300', sky: 'text-sky-600 dark:text-sky-400' } as const;
  return (
    <div className={cn('mt-3 flex gap-2.5 rounded-card border p-3', TONES[tone])}>
      <span className={cn('mt-0.5 shrink-0 [&>svg]:size-4', ICON[tone])}>{icon}</span>
      <div className="min-w-0">
        <div className="type-body-strong text-content">{title}</div>
        {children && <div className="mt-0.5 type-caption text-muted">{children}</div>}
      </div>
    </div>
  );
}

/* ─────────────────────────────── the modal ─────────────────────────────── */

// Status → the tint of the summary band, so the verdict for the day is legible before any reading.
const BAND: Record<string, string> = {
  PRESENT: 'border-brand-500/30 bg-brand-50/70 dark:bg-brand-900/20',
  HALF_DAY: 'border-harvest-400/40 bg-harvest-50/70 dark:bg-harvest-400/10',
  LEAVE: 'border-sky-500/30 bg-sky-500/8',
  ABSENT: 'border-danger-500/30 bg-danger-500/8',
  HOLIDAY: 'border-line bg-surface-2',
};

/**
 * ONE ATTENDANCE RECORD, in a centred dialog.
 *
 * It replaced a 460px slide-over that listed fifteen key/value rows at one weight — status, GPS,
 * selfies and admin controls all equally loud — so answering "is this punch trustworthy, and did the
 * rep actually do anything today?" meant reading the whole thing and then leaving the screen to look
 * up their visits.
 *
 * The dialog answers in the order the questions get asked: the VERDICT band (status · how it was
 * recorded · hours · whether anything needs review), then THE SHIFT (both punches with their selfie
 * and address), then FIELD WORK (visits and the orders that came out of them), then the INTEGRITY
 * checks, then the MAP of where the rep actually was. Admin overrides live in the footer, so they
 * stay reachable without scrolling and never read as data.
 */
export function AttendanceDetailModal({ id, onClose, canManage }: { id: string | null; onClose: () => void; canManage: boolean }) {
  const qc = useQueryClient();
  const canViewVisits = usePermission('VISIT.VIEW');
  const canViewOrders = usePermission('ORDER.VIEW');

  const { data: a, isLoading } = useQuery({
    queryKey: ['attendance-detail', id],
    queryFn: () => request<any>({ url: `/attendance/${id}` }),
    enabled: !!id,
  });
  const [newStatus, setNewStatus] = useState<string | undefined>();
  useEffect(() => { setNewStatus(undefined); }, [id]); // a different row opens with a clean override box

  const userId = a?.userId ? String(a.userId) : undefined;
  const logDate = a?.logDate ? String(a.logDate) : undefined;

  // What the rep actually did that day. Both are day-scoped and hierarchy-scoped server-side, and
  // both are permission-gated separately from attendance — a reviewer without VISIT.VIEW simply
  // doesn't get the section rather than a 403 in the console.
  const { data: visits } = useQuery({
    queryKey: ['attendance-day-visits', userId, logDate],
    queryFn: () => request<any[]>({ url: '/visits', params: { userId, date: logDate } }),
    enabled: !!userId && !!logDate && canViewVisits,
  });
  const { data: orders } = useQuery({
    queryKey: ['attendance-day-orders', userId, logDate],
    queryFn: () => requestAllPages('/orders', { salesPersonId: userId, from: logDate, to: logDate, includeClosed: 'true' }),
    enabled: !!userId && !!logDate && canViewOrders,
  });

  const dayVisits = visits ?? [];
  const dayOrders = (orders?.data ?? []) as any[];
  // Orders raised from a visit are shown ON that visit; only the rest need a row of their own, or the
  // same order would be counted twice on screen.
  const visitOrderIds = new Set(dayVisits.map((v: any) => String(v.order?.id ?? v.orderId ?? '')).filter(Boolean));
  const looseOrders = dayOrders.filter((o: any) => !visitOrderIds.has(String(o.id)));
  const orderValue = dayOrders.reduce((s: number, o: any) => s + Number(o.grandTotal ?? 0), 0);

  // ── Last location: the rep's latest activity that day (a punch OR a visit) with coordinates. ──
  const lastLat = a?.lastLat ?? a?.checkOutLat ?? a?.checkInLat;
  const lastLng = a?.lastLng ?? a?.checkOutLong ?? a?.checkInLong;
  const lastAt = a?.lastActivityAt ?? a?.checkOutAt ?? a?.checkInAt;
  const lastType = a?.lastActivityType ?? (a?.checkOutAt ? 'Check-out' : a?.checkInAt ? 'Check-in' : null);
  // Each punch gets its OWN address — "where they clocked in" and "where they clocked out" are
  // different questions. The lookup is cached per coordinate, so a repeated spot costs one call.
  const inGeo = useReverseGeocode(a?.checkInLat, a?.checkInLong);
  const outGeo = useReverseGeocode(a?.checkOutLat, a?.checkOutLong);
  const lastGeo = useReverseGeocode(lastLat, lastLng);

  const flags: string[] = a?.anomalyFlags ?? [];
  // Everything a reviewer would call a problem, counted once: the band shows how many, the checks
  // below show which. Geofence only counts when there IS a punch to be inside or outside of.
  const issues = useMemo(() => {
    if (!a) return [] as string[];
    return [
      a.checkInAt && a.isWithinGeofence === false ? 'Punched outside the geofence' : null,
      a.isMockLocation ? 'Mock (spoofed) location' : null,
      a.missingCheckout ? 'Never checked out' : null,
      ...flags.map((f) => f.replace(/_/g, ' ').toLowerCase()),
    ].filter(Boolean) as string[];
  }, [a, flags]);

  const points = useMemo(() => {
    const seen = new Set<string>();
    const out: Array<{ pt: [number, number]; icon: L.DivIcon; label: string }> = [];
    const add = (lat: any, lng: any, icon: L.DivIcon, label: string) => {
      if (lat == null || lng == null) return;
      const p: [number, number] = [Number(lat), Number(lng)];
      if (!Number.isFinite(p[0]) || !Number.isFinite(p[1]) || (p[0] === 0 && p[1] === 0)) return;
      const k = `${p[0].toFixed(5)},${p[1].toFixed(5)}`;
      if (seen.has(k)) return; // the same spot pinned twice would just hide one marker under another
      seen.add(k);
      out.push({ pt: p, icon, label });
    };
    // Every shift's punches, not only the day's first and last: the punch-out before a break and the
    // punch-in after it are places the rep really was.
    const shifts: any[] = a?.sessions?.length ? a.sessions : [a];
    const nth = (k: number) => (shifts.length > 1 ? ` (shift ${k + 1})` : '');
    shifts.forEach((s: any, k: number) => {
      add(s?.checkInLat, s?.checkInLong, IN_ICON, `Check-in${nth(k)}${s?.checkInAt ? ` · ${formatIstTime(s.checkInAt)}` : ''}`);
    });
    for (const v of dayVisits) {
      add(v.checkInLat, v.checkInLong, VISIT_ICON, `${v.party?.name ?? 'Visit'}${v.checkInAt ? ` · ${formatIstTime(v.checkInAt)}` : ''}`);
    }
    shifts.forEach((s: any, k: number) => {
      add(s?.checkOutLat, s?.checkOutLong, OUT_ICON, `Check-out${nth(k)}${s?.checkOutAt ? ` · ${formatIstTime(s.checkOutAt)}` : ''}`);
    });
    add(lastLat, lastLng, LAST_ICON, `${lastType ?? 'Last seen'}${lastAt ? ` · ${formatIstTime(lastAt)}` : ''}`);
    return out;
  }, [a, dayVisits, lastLat, lastLng, lastAt, lastType]);

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['attendance-detail', id] });
    qc.invalidateQueries({ queryKey: ['attendance'] });
    qc.invalidateQueries({ queryKey: ['badges'] });
  };
  const setStatusMut = useConfirmedMutation({ title: "Change this day’s attendance?", content: "The change is recorded against your name, and it feeds pay and expense claims.", okText: "Yes, change it" }, {
    mutationFn: (s: string) => request({ url: `/attendance/${id}/status`, method: 'PATCH', data: { status: s } }),
    onSuccess: () => { message.success('Status updated'); setNewStatus(undefined); refresh(); },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Failed'),
  });
  const clearMut = useMutation({
    mutationFn: () => request({ url: `/attendance/${id}/clear-anomalies`, method: 'PATCH', data: {} }),
    onSuccess: () => { message.success('Anomalies cleared'); refresh(); },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Failed'),
  });

  // SOURCE_TAG has no SELF entry (the table needs no chip for it) — the modal still says plainly
  // that the rep punched this themselves, because "who recorded it" is the first trust question.
  const sourceChip = a && (SOURCE_TAG[a.source] ?? (a.source === 'SELF' ? { text: 'Rep, on their device', color: 'green' as const } : null));
  const sameDay = (at?: string | null) => !at || !logDate || dayjs(at).format('YYYY-MM-DD') === logDate;

  return (
    <Modal
      open={!!id}
      onOpenChange={(o) => { if (!o) onClose(); }}
      width={860}
      title={a ? (
        <span className="flex min-w-0 flex-wrap items-center gap-2">
          <span className="truncate">{a.staffName ?? `#${a.userId}`}</span>
          <RoleBadge role={a.staffRole} />
        </span>
      ) : 'Attendance record'}
      description={logDate ? dayjs(logDate).format('dddd, DD MMMM YYYY') : undefined}
      footer={
        <>
          {canManage && a && (
            <div className="mr-auto flex flex-wrap items-center gap-2">
              <Select
                style={{ width: 168 }} placeholder="Override status…"
                value={newStatus ?? a.status}
                onChange={setNewStatus}
                options={STATUS_OPTIONS.map((s) => ({ value: s, label: s.replace('_', ' ') }))}
              />
              <Button variant="primary" loading={setStatusMut.isPending}
                disabled={!newStatus || newStatus === a.status}
                onClick={() => newStatus && setStatusMut.mutate(newStatus)}>Apply</Button>
              {flags.length > 0 && (
                <Popconfirm title="Clear anomaly flags?" description="Marks this record as reviewed." onConfirm={() => clearMut.mutate()}>
                  <Button variant="danger" loading={clearMut.isPending} icon={<AlertTriangle size={16} />}>Clear anomalies</Button>
                </Popconfirm>
              )}
            </div>
          )}
          <Button onClick={onClose}>Close</Button>
        </>
      }
    >
      {isLoading || !a ? (
        <div className="grid place-items-center py-16"><Spin /></div>
      ) : (
        <>
          {/* ── Verdict band: status, how it was recorded, hours, and whether anything needs review ── */}
          {/* Three equal columns rather than a left-packed row: the band spans the dialog, so
              clustering everything at one end left half of it empty and the eye with nothing to
              land on. Dividers come from `divide-x`, so they can never drift out of step. */}
          <div className={cn('grid gap-3 rounded-card border p-3.5 sm:grid-cols-3 sm:gap-0 sm:divide-x sm:divide-line',
            BAND[a.status] ?? 'border-line bg-surface-2')}>
            <div className="min-w-0 sm:pr-4">
              <div className="type-overline text-subtle">Status</div>
              <div className="mt-1 flex flex-wrap items-center gap-1.5">
                <Tag tone={STATUS_COLOR[a.status] ?? 'neutral'}>{String(a.status).replace('_', ' ')}</Tag>
                {sourceChip && <Tag tone={sourceChip.color}>{sourceChip.text}</Tag>}
              </div>
            </div>

            <div className="min-w-0 sm:px-4">
              <div className="type-overline text-subtle">Hours worked</div>
              <div className="mt-0.5 type-metric-sm text-content">
                {a.checkInAt && !a.checkOutAt ? 'Open shift' : fmtWorked(a.workedMin)}
              </div>
            </div>

            <div className="min-w-0 sm:pl-4">
              <div className="type-overline text-subtle">Review</div>
              <div className={cn('mt-1 flex items-start gap-1.5 type-body-strong',
                issues.length ? 'text-danger-600 dark:text-danger-500' : 'text-brand-700 dark:text-brand-300')}>
                {issues.length ? <AlertTriangle className="mt-0.5 size-4 shrink-0" /> : <CheckCircle2 className="mt-0.5 size-4 shrink-0" />}
                <span className="min-w-0">
                  {issues.length ? `${issues.length} thing${issues.length > 1 ? 's' : ''} to check` : 'Nothing flagged'}
                  {/* Naming them here saves scrolling to the checks below just to learn WHAT is wrong. */}
                  {issues.length > 0 && <span className="block type-caption font-normal text-muted">{issues.join(' · ')}</span>}
                </span>
              </div>
            </div>
          </div>

          {/* ── Things a reviewer must not miss, said in words rather than left as a chip ── */}
          {a.source === 'ON_BEHALF' && (
            <Callout tone="plum" icon={<UserCog />}
              title={`Marked on behalf by ${a.markedByName ?? (a.markedBy ? `#${a.markedBy}` : 'an unknown user')}`}>
              {/* ON_BEHALF is the one path that bypasses the device-GPS anti-fraud gate, so the reason
                  belongs on screen, not only in the audit log. */}
              {a.onBehalfReason || 'No reason recorded (this record predates the mandatory reason).'}
            </Callout>
          )}
          {a.source === 'AUTO' && (
            <Callout tone="gold" icon={<Timer />} title="Closed automatically at 22:00">
              The rep never punched out{a.autoCheckoutIdleMin
                ? `; the shift was stamped at their last activity, ${fmtIdle(a.autoCheckoutIdleMin)} before the cut-off.`
                : '.'}
            </Callout>
          )}
          {a.missingCheckout && a.source !== 'AUTO' && (
            <Callout tone="sky" icon={<Clock />} title="Missing check-out">
              Checked in on a past day and never checked out — needs a regularization to close the shift.
            </Callout>
          )}
          {(a.source === 'MANUAL' || a.source === 'ON_BEHALF') && a.checkInAddress && (
            <Callout tone="sky" icon={<UserCog />} title="Note on the manual entry">{a.checkInAddress}</Callout>
          )}

          {/* ── The shift(s), end to end ── */}
          {(a.sessions?.length ?? 0) > 1 ? (
            /* Several shifts — out for lunch, back in for the afternoon. One punch-in/punch-out pair
               would show only the first check-in and the last check-out and hide the gap, so each shift
               gets its own line, and the hours above are these added up. */
            <Section title={`The shifts · ${a.sessions.length}`}
              hint="Every punch-in with its punch-out. Hours worked add the shifts up; the gaps between them are not counted.">
              <div className="overflow-hidden rounded-card border border-line">
                {a.sessions.map((s: any, i: number) => {
                  const running = !s.checkOutAt && i === a.sessions.length - 1;
                  const mins = s.checkInAt && s.checkOutAt
                    ? Math.max(0, Math.round((new Date(s.checkOutAt).getTime() - new Date(s.checkInAt).getTime()) / 60000))
                    : null;
                  const chip = SOURCE_TAG[s.source];
                  const inMap = mapsUrl(s.checkInLat, s.checkInLong);
                  return (
                    <div key={s.id ?? i} className="flex items-center gap-3 border-b border-line px-3 py-2.5 last:border-b-0">
                      <span className="grid size-6 shrink-0 place-items-center rounded-full bg-surface-2 type-caption-strong text-muted">{i + 1}</span>
                      <div className="min-w-0 flex-1">
                        <div className="type-body-strong tabular-nums text-content">
                          {formatIstTime(s.checkInAt)} → {s.checkOutAt ? formatIstTime(s.checkOutAt) : (running ? 'still open' : 'no punch-out recorded')}
                        </div>
                        <div className="mt-0.5 flex flex-wrap items-center gap-1.5 type-caption text-subtle">
                          {s.source === 'AUTO'
                            ? <Tag tone="gold">Closed automatically at 22:00</Tag>
                            : chip ? <Tag tone={chip.color}>{chip.text}</Tag> : <span>Punched on the device</span>}
                          {s.isWithinGeofence === false && <Tag tone="red">Outside geofence</Tag>}
                          {inMap && (
                            <a href={inMap} target="_blank" rel="noreferrer"
                              className="inline-flex items-center gap-1 text-brand-700! hover:underline dark:text-brand-400!">
                              Punch-in on the map <ExternalLink className="size-3" />
                            </a>
                          )}
                        </div>
                      </div>
                      <span className="shrink-0 type-body-strong tabular-nums text-muted">{mins != null ? fmtWorked(mins) : '—'}</span>
                    </div>
                  );
                })}
              </div>
            </Section>
          ) : (
          <Section title="The shift" hint="Both punches, with the selfie and the place each was taken.">
            <div className="grid gap-3 sm:grid-cols-[1fr_auto_1fr] sm:items-center">
              <PunchCard kind="in" at={a.checkInAt} sameDay={sameDay(a.checkInAt)} selfie={a.checkInSelfieUrl}>
                <Where lat={a.checkInLat} lng={a.checkInLong}
                  // A SELF/AUTO punch carries the address the device captured; on a MANUAL row the
                  // same column holds the entry NOTE, which is shown in its own callout instead.
                  address={inGeo.data ?? (a.source === 'SELF' || a.source === 'AUTO' ? a.checkInAddress : null)}
                  resolving={inGeo.isFetching} />
              </PunchCard>

              <div className="relative flex items-center justify-center py-1 sm:px-1">
                <span className="absolute inset-x-0 top-1/2 hidden -translate-y-1/2 border-t border-dashed border-line sm:block" />
                <span className="relative inline-flex items-center gap-1 rounded-pill border border-line bg-surface px-2.5 py-1 type-caption-strong text-muted">
                  <Clock className="size-3" />{a.checkInAt && !a.checkOutAt ? 'Open' : fmtWorked(a.workedMin)}
                </span>
              </div>

              <PunchCard kind="out" at={a.checkOutAt} sameDay={sameDay(a.checkOutAt)} selfie={a.checkOutSelfieUrl}>
                <Where lat={a.checkOutLat} lng={a.checkOutLong} address={outGeo.data} resolving={outGeo.isFetching} />
              </PunchCard>
            </div>
          </Section>
          )}

          {/* ── What the hours produced: visits, and the orders that came out of them ── */}
          {(canViewVisits || canViewOrders) && (
            <Section
              title="Field work that day"
              hint="Customers visited between the punches, and the orders booked."
              right={
                <div className="flex flex-wrap items-center gap-1.5">
                  {canViewVisits && <Tag tone="neutral">{dayVisits.length} visit{dayVisits.length === 1 ? '' : 's'}</Tag>}
                  {canViewOrders && <Tag tone="neutral">{dayOrders.length} order{dayOrders.length === 1 ? '' : 's'}</Tag>}
                  {canViewOrders && orderValue > 0 && <Tag tone="green">{inr(orderValue)}</Tag>}
                </div>
              }
            >
              {dayVisits.length === 0 && looseOrders.length === 0 ? (
                <div className="rounded-card border border-dashed border-line px-3 py-5 text-center type-caption text-subtle">
                  No visits or orders recorded on this day.
                </div>
              ) : (
                <div className="divide-y divide-line overflow-hidden rounded-card border border-line">
                  {dayVisits.map((v: any) => {
                    const isFarmer = v.visitType === 'FARMER';
                    return (
                      <div key={v.id} className="flex flex-wrap items-center gap-x-3 gap-y-1.5 px-3 py-2.5">
                        <span className={cn('grid size-7 shrink-0 place-items-center rounded-full [&>svg]:size-3.5',
                          isFarmer ? 'bg-plum-500/10 text-plum-500 dark:text-plum-400' : 'bg-sky-500/10 text-sky-600 dark:text-sky-400')}>
                          {isFarmer ? <Sprout /> : <Store />}
                        </span>
                        <div className="min-w-0 flex-1">
                          {/* Wraps: a dealer's name is how the reviewer knows the visit, and on a phone a
                              truncated "SHREE GAJANAN AGRO SER…" hides the half that tells two apart. */}
                          <div className="wrap-break-word type-body-medium text-content">
                            {v.visitType === 'RETAILER' && v.retailerId
                              ? <RetailerLink id={v.retailerId}>{v.party?.name ?? 'Retailer'}</RetailerLink>
                              : (v.party?.name ?? (isFarmer ? 'Farmer' : 'Visit'))}
                          </div>
                          <div className="truncate type-caption text-subtle">
                            {[
                              v.checkInAt && `${formatIstTime(v.checkInAt)}${v.checkOutAt ? `–${formatIstTime(v.checkOutAt)}` : ''}`,
                              v.durationMin ? `${v.durationMin}m` : null,
                              v.party?.location,
                            ].filter(Boolean).join(' · ')}
                          </div>
                        </div>
                        {v.checkInWithinGeofence === false && <Tag tone="red"><MapPin className="size-3" />Out</Tag>}
                        {v.order
                          ? (
                            <Tag tone="green">
                              <PackageCheck className="size-3" />
                              <OrderLink id={v.order.id}>{v.order.orderNo}</OrderLink>
                              {v.order.grandTotal != null ? ` · ${inr(v.order.grandTotal)}` : ''}
                            </Tag>
                          )
                          : <span className="type-caption text-subtle">No order</span>}
                      </div>
                    );
                  })}

                  {/* Orders booked without a visit attached — a phone/office order still belongs to the
                      day's output, and leaving it out would understate what the rep produced. */}
                  {looseOrders.map((o: any) => (
                    <div key={o.id} className="flex flex-wrap items-center gap-x-3 gap-y-1.5 bg-surface-2/40 px-3 py-2.5">
                      <span className="grid size-7 shrink-0 place-items-center rounded-full bg-brand-50 text-brand-700 dark:bg-brand-900/30 dark:text-brand-300">
                        <PackageCheck className="size-3.5" />
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="truncate type-body-medium text-content">
                          <OrderLink id={o.id}>{o.orderNo}</OrderLink>
                        </div>
                        <div className="truncate type-caption text-subtle">
                          {[o.retailerName, o.channel, 'no visit linked'].filter(Boolean).join(' · ')}
                        </div>
                      </div>
                      <Tag tone="neutral">{orderStatusLabel(o.status)}</Tag>
                      <span className="type-body-medium tabular-nums text-content">{inr(o.grandTotal)}</span>
                    </div>
                  ))}
                </div>
              )}
            </Section>
          )}

          {/* ── Integrity: the anti-fraud signals, each as a verdict ── */}
          <Section title="Integrity checks" hint="How far the device evidence behind this record can be trusted.">
            <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
              <Check icon={<MapPin />} label="Geofence"
                value={!a.checkInAt ? 'No punch' : a.isWithinGeofence ? 'Inside' : 'Outside'}
                tone={!a.checkInAt ? 'idle' : a.isWithinGeofence ? 'good' : 'bad'} />
              <Check icon={a.isMockLocation ? <ShieldAlert /> : <ShieldCheck />} label="Mock location"
                value={a.isMockLocation ? 'Detected' : 'Not detected'} tone={a.isMockLocation ? 'bad' : 'good'} />
              {/*
                THE PHOTO COMES FIRST, and the check says what it MEANS rather than printing a number.

                It used to render the raw score — "Face match 0.93" — in green, under a heading
                promising to say how far the evidence can be trusted. Three things were wrong with
                that. A bare decimal names no verdict: 0.93 out of what, and is that good? Nothing
                computed it (the web punch posted the constant 0.93 on every check-in). And the punch
                that carried it took no photo at all, so there was never a face to match.

                So it reads the evidence in order: no photo → there is nothing to verify and the
                reviewer should know that; a photo with no score → there is something to LOOK at, and
                the dialog shows it in the shift card above; a photo with a score → say whether it is
                the rep, and keep the number as supporting detail.

                The pass/fail line is the SERVER's — `FACE_MISMATCH` is raised against
                FACE_MATCH_THRESHOLD at punch time — never a threshold re-guessed here, which would
                drift the day that env var changes.
              */}
              {(() => {
                const score = a.checkInFaceScore != null ? Number(a.checkInFaceScore) : null;
                const pct = score != null ? `${Math.round(score * 100)}%` : '';
                const mismatch = flags.includes('FACE_MISMATCH');
                const [value, title, tone] = !a.checkInSelfieUrl
                  ? ['No photo taken',
                    'This punch was recorded without a selfie, so there is no face to check. Judge it on the location evidence instead.',
                    'idle' as const]
                  : score == null
                    ? ['Photo, not matched',
                      'A selfie was taken and is shown above — look at it yourself. Nothing compared it to the rep on file: automatic face matching is not live yet.',
                      'idle' as const]
                    : mismatch
                      ? [`Face differs (${pct})`,
                        `The selfie scored ${pct} against the rep's photo on file, below the threshold — this may not be the same person.`,
                        'bad' as const]
                      : [`Face matches (${pct})`,
                        `The selfie scored ${pct} against the rep's photo on file, above the threshold.`,
                        'good' as const];
                return <Check icon={<ScanFace />} label="Selfie check" value={value} title={title} tone={tone} />;
              })()}
              <Check icon={<Crosshair />} label="GPS accuracy"
                value={a.checkInAccuracyM != null ? `±${a.checkInAccuracyM} m` : 'Unknown'}
                tone={a.checkInAccuracyM == null ? 'idle' : a.checkInAccuracyM <= 50 ? 'good' : 'bad'} />
            </div>
            <div className="mt-2 flex flex-wrap items-center gap-1.5 rounded-control border border-line bg-surface-2/50 px-2.5 py-2">
              <span className="type-caption text-subtle">Anomalies</span>
              {flags.length
                ? flags.map((f) => <Tag tone="red" key={f}><AlertTriangle className="size-3" />{f.replace(/_/g, ' ')}</Tag>)
                : <Tag tone="green"><CheckCircle2 className="size-3" />None raised</Tag>}
            </div>
          </Section>

          {/* ── Where they actually were ── */}
          {points.length > 0 && (
            <Section title="On the map" hint="Green is the check-in, violet each visit, red the check-out, blue the last activity.">
              <div className="overflow-hidden rounded-card border border-line">
                <div className="h-55 w-full">
                  <MapContainer center={points[0].pt} zoom={15} style={{ height: '100%', width: '100%' }} scrollWheelZoom={false}>
                    <TileLayer url={MAP_TILE_URL} attribution={MAP_TILE_ATTRIBUTION} />
                    <FitPoints points={points.map((p) => p.pt)} />
                    {points.length > 1 && (
                      <Polyline positions={points.map((p) => p.pt)}
                        pathOptions={{ color: PALETTE.sky[500], weight: 2, dashArray: '5 6', opacity: 0.75 }} />
                    )}
                    {points.map((p) => (
                      <Marker key={p.label} position={p.pt} icon={p.icon}>
                        <LTooltip direction="top" offset={[0, -22]}>{p.label}</LTooltip>
                      </Marker>
                    ))}
                  </MapContainer>
                </div>
                <div className="flex flex-wrap items-center justify-between gap-2 border-t border-line bg-surface-2/50 px-3 py-2">
                  <div className="min-w-0">
                    <div className="type-overline text-subtle">Last known position</div>
                    <div className="mt-0.5 line-clamp-2 type-caption text-content">
                      {lastGeo.isFetching
                        ? 'Resolving address…'
                        : (lastGeo.data || (lastLat ? `${Number(lastLat).toFixed(5)}, ${Number(lastLng).toFixed(5)}` : '—'))}
                    </div>
                    <div className="type-caption text-subtle">{lastType ?? 'Location'}{lastAt ? ` · ${formatIst(lastAt)}` : ''}</div>
                  </div>
                  <Button variant="ghost" size="sm" icon={<ExternalLink size={14} />}
                    href={mapsUrl(lastLat, lastLng)} target="_blank" rel="noreferrer">
                    Google Maps
                  </Button>
                </div>
              </div>
            </Section>
          )}
        </>
      )}
    </Modal>
  );
}
